import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class CommentSearchDataUser {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  second_name: string;

  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @IsString()
  @IsOptional()
  logo?: string;
}

export class CommentSearchDataAffiliation {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['news', 'group'])
  @IsString()
  @IsNotEmpty()
  name: 'news' | 'group';
}

export class CommentSearchData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => CommentSearchDataAffiliation)
  @IsNotEmpty()
  affiliation: CommentSearchDataAffiliation;

  @ValidateNested()
  @Type(() => CommentSearchDataUser)
  @IsNotEmpty()
  user: CommentSearchDataUser;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString()
  @IsNotEmpty()
  target_name: string;

  @IsString()
  @IsNotEmpty()
  text: string;
}

export class ContentSearchResponseComments {
  @ValidateNested({ each: true })
  @Type(() => CommentSearchData)
  @IsArray()
  @IsNotEmpty()
  items: CommentSearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class NewsSearchData {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  title: string;

  @IsDateString()
  @IsNotEmpty()
  date: string;

  @IsString()
  @IsNotEmpty()
  category: string;

  @IsUUID()
  @IsOptional()
  cover_att_id?: string;

  @IsString()
  @IsNotEmpty()
  announcement: string;
}

export class ContentSearchResponseNews {
  @ValidateNested({ each: true })
  @Type(() => NewsSearchData)
  @IsArray()
  @IsNotEmpty()
  items: NewsSearchData[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class ContentSearchResponse {
  @ValidateNested()
  @Type(() => ContentSearchResponseNews)
  @IsNotEmpty()
  news: ContentSearchResponseNews;

  @ValidateNested()
  @Type(() => ContentSearchResponseComments)
  @IsNotEmpty()
  comments: ContentSearchResponseComments;
}

export class ContentSearchRequestLimits {
  @IsInt()
  @IsNotEmpty()
  news: number;

  @IsInt()
  @IsNotEmpty()
  comments: number;
}

export class ContentSearchRequest {
  @IsString()
  @IsOptional()
  query?: string;

  @IsIn(['news', 'comments'])
  @IsString()
  @IsOptional()
  group?: 'news' | 'comments';

  @ValidateNested()
  @Type(() => ContentSearchRequestLimits)
  @IsNotEmpty()
  limits: ContentSearchRequestLimits;
}

@injectable()
export class SearchService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async contentSearch({ body }: { body: ContentSearchRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, ContentSearchRequest),
    ]);

    let requestPath = '/content/search';

    const result = await this.http.post(requestPath, body, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      ContentSearchResponse,
    );

    return response;
  }
}
